import React from 'react';
// CB:BEGIN v2-active-project-timeline
import ProjectJourneyTimeline from '../ProjectJourneyTimeline';
// CB:END v2-active-project-timeline

interface AcceptedBid {
  totalProjectAmount?: number;
  amount?: number;
}

interface EscrowAccount {
  totalDeposited?: number;
  funded?: boolean;
}

interface ActiveProject {
  id: string;
  address: string;
  projectType?: string;
  status: string;
  acceptedBid?: AcceptedBid;
  escrowAccount?: EscrowAccount;
}

interface ActiveProjectCardProps {
  project: ActiveProject;
  onNavigate: (screen: string) => void;
}

/**
 * Displays a single active project card in the contractor dashboard.
 */
export default function ActiveProjectCard({ project, onNavigate }: ActiveProjectCardProps) {
  const rawTotalCents = project.acceptedBid?.totalProjectAmount ?? project.acceptedBid?.amount ?? 0;
  const rawBidCents   = project.acceptedBid?.amount ?? 0;
  let bidAmountCents  = rawTotalCents;
  if (rawBidCents > 0 && (rawTotalCents === 0 || rawTotalCents < rawBidCents * 1.05)) {
    bidAmountCents = Math.round(rawBidCents * 1.1);
  }
  const bidAmount = bidAmountCents / 100;

  const STATUS_LABELS: Record<string, string> = {
    BID_SELECTED: 'Active',
    ACTIVE_P1:   'Active - Phase 1',
    ACTIVE_P2:   'Active - Phase 2',
    ACTIVE_P3:   'Active - Phase 3',
    IN_PROGRESS: 'In Progress',
    COMPLETED:   'Completed',
    SCHEDULED:   'Scheduled',
    HOMEOWNER_CONTRACT_SIGNING: 'Contract Signing',
    ESCROW_PENDING: 'Awaiting Funding',
  };
  const displayStatus = STATUS_LABELS[project.status] ?? project.status ?? 'Active';
  const isCompleted   = project.status === 'COMPLETED';

  return (
    <div
      style={{
        border: `1px solid ${isCompleted ? '#6b7280' : '#10b981'}`,
        backgroundColor: isCompleted ? '#f9fafb' : '#f0fdf4',
        borderRadius: 8,
        padding: 12,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          <h4 style={{ margin: '0 0 4px 0', fontSize: 16 }}>
            {project.projectType ? project.projectType.charAt(0).toUpperCase() + project.projectType.slice(1).toLowerCase() : 'Roof'} Replacement
          </h4>
          <p style={{ color: '#6b7280', margin: 0, fontSize: 14 }}>{project.address}</p>
        </div>
        <span className="status-badge status-success" style={{ fontSize: 11, padding: '2px 6px' }}>
          Accepted
        </span>
      </div>

      {/* CB:BEGIN v2-active-project-timeline */}
      <div style={{ marginTop: 10, borderTop: '1px solid #e5e7eb', paddingTop: 10 }}>
        <ProjectJourneyTimeline
          projectStatus={project.status}
          role="contractor"
          compact
          minimumStage={project.escrowAccount?.funded ? 'work_in_progress' : undefined}
        />
      </div>
      {/* CB:END v2-active-project-timeline */}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
        <span style={{ color: '#6b7280', fontSize: 12 }}>{displayStatus}</span>
        <span style={{ color: '#10b981', fontWeight: 600, fontSize: 14 }}>
          ${bidAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      </div>

      <div style={{ marginTop: 12, borderTop: '1px solid #e5e7eb', paddingTop: 10 }}>
        <button
          type="button"
          onClick={() => onNavigate(`/contractor/projects/${project.id}`)}
          style={{
            width: '100%',
            padding: '8px 0',
            background: '#2563eb',
            color: 'white',
            border: 'none',
            borderRadius: 7,
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          View Project
        </button>
      </div>
    </div>
  );
}
